import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchIncentiveTicketById, updateTicketStatus, addTicketMember, removeTicketMember, setSalesOwner, validateAndCalculate, fetchEmployees, applyPendingSyncUpdate, fetchSyncChanges, deleteIncentiveTicket, rollbackTicketWorkflow } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';
import TicketPrintTemplate from './TicketPrintTemplate';
import SyncStatusBadge from '../../components/ui/SyncStatusBadge';
import { supabase } from '../../lib/supabase';
import RollbackWorkflowDialog from './components/RollbackWorkflowDialog';

const STATUS_COLORS = {
  'Draft': 'bg-muted text-muted-foreground border-border',
  'Pending Team Assignment': 'bg-warning/15 text-warning border-warning/30',
  'Calculated': 'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'Pending Approval': 'bg-orange-500/15 text-orange-400 border-orange-500/30',
  'Approved': 'bg-success/15 text-success border-success/30',
  'On Hold': 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
  'Ready for Payment': 'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'Paid': 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  'Rejected': 'bg-error/15 text-error border-error/30',
  'Cancelled': 'bg-muted text-muted-foreground border-border',
};

const STATUS_STEPS = [
  'Pending Team Assignment',
  'Calculated',
  'Pending Approval',
  'Approved',
  'Ready for Payment',
  'Paid',
];

// Employee dropdown selector — shared pool for both Sales Deal Owner and Project Team Members
const EmployeeSelect = ({ employees, value, onChange, placeholder = 'Select employee...' }) => {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);

  const filtered = (employees || [])?.filter(e =>
    (e?.fullName || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (e?.department || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (e?.role || '')?.toLowerCase()?.includes(search?.toLowerCase()) ||
    (e?.employeeCode || '')?.toLowerCase()?.includes(search?.toLowerCase())
  );

  const selected = (employees || [])?.find(e => e?.id === value);
  const selectedDept = selected?.department || 'General';
  const selectedRole = selected?.role || 'Employee';

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 hover:border-primary/50 transition-colors"
      >
        <span className={selected ? 'text-foreground' : 'text-muted-foreground'}>
          {selected ? `${selected?.fullName} — ${selectedDept} · ${selectedRole}` : placeholder}
        </span>
        <Icon name="ChevronDown" size={14} className="text-muted-foreground flex-shrink-0" />
      </button>
      {open && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-card border border-border rounded-xl shadow-xl max-h-64 overflow-hidden">
          <div className="p-2 border-b border-border">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e?.target?.value)}
              placeholder="Search by name, department, role..."
              className="w-full px-3 py-2 bg-background border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
            />
          </div>
          <div className="overflow-y-auto max-h-48">
            {filtered?.length === 0 ? (
              <div className="p-3 text-center text-sm text-muted-foreground">No employees found</div>
            ) : (
              filtered?.map(emp => {
                const dept = emp?.department || 'General';
                const role = emp?.role || 'Employee';
                return (
                  <button
                    key={emp?.id}
                    type="button"
                    onClick={() => { onChange(emp); setOpen(false); setSearch(''); }}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-sm hover:bg-muted/50 transition-colors text-left ${value === emp?.id ? 'bg-primary/10 text-primary' : 'text-foreground'}`}
                  >
                    <div className="flex flex-col">
                      <span className="font-medium">{emp?.fullName}</span>
                      {emp?.employeeCode && <span className="text-xs text-muted-foreground">{emp?.employeeCode}</span>}
                    </div>
                    <span className="text-xs text-muted-foreground text-right">{dept} · {role}</span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Add Member Modal with employee dropdown
const AddMemberModal = ({ isOpen, onClose, onAdd, employees }) => {
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [workDescription, setWorkDescription] = useState('');
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!selectedEmployee) newErrors.employee = 'Please select an employee';
    setErrors(newErrors);
    if (Object.keys(newErrors)?.length > 0) return;
    setSaving(true);
    try {
      await onAdd({
        employeeId: selectedEmployee?.id,
        employeeName: selectedEmployee?.fullName,
        role: selectedEmployee?.role,
        department: selectedEmployee?.department,
        workDescription,
      });
      setSelectedEmployee(null);
      setWorkDescription('');
      onClose();
    } catch (err) {
      setErrors({ submit: err?.message });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full">
        <div className="flex items-center justify-between p-5 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
              <Icon name="UserPlus" size={18} color="white" />
            </div>
            <h3 className="font-semibold text-foreground">Add Project Team Member</h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Select Employee *</label>
            <EmployeeSelect
              employees={employees}
              value={selectedEmployee?.id}
              onChange={setSelectedEmployee}
              placeholder="Select from Employee Pool..."
            />
            {errors?.employee && <p className="text-xs text-error mt-1">{errors?.employee}</p>}
          </div>
          {selectedEmployee && (
            <div className="p-3 bg-muted/20 rounded-xl border border-border/50 text-sm">
              <div className="flex items-center gap-2">
                <Icon name="User" size={14} className="text-primary" />
                <span className="font-medium text-foreground">{selectedEmployee?.fullName}</span>
                {selectedEmployee?.employeeCode && <span className="text-xs text-muted-foreground font-mono">({selectedEmployee?.employeeCode})</span>}
              </div>
              <div className="flex gap-4 mt-1 text-xs text-muted-foreground">
                <span>{selectedEmployee?.department || 'General'}</span>
                <span>·</span>
                <span>{selectedEmployee?.role || 'Employee'}</span>
              </div>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">Work Description</label>
            <textarea
              value={workDescription}
              onChange={(e) => setWorkDescription(e?.target?.value)}
              placeholder="Describe the work contribution..."
              rows={3}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>
          {errors?.submit && (
            <div className="p-3 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
              {errors?.submit}
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Adding...' : 'Add Member'}
          </Button>
        </div>
      </div>
    </div>
  );
};

const ActionModal = ({ isOpen, onClose, onConfirm, title, description, actionLabel, variant = 'default', requiresComment = false, requiresPaymentRef = false, requiresPaymentDate = false }) => {
  const [comment, setComment] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleConfirm = async () => {
    if (requiresComment && !comment?.trim()) {
      setError('Comment is required');
      return;
    }
    if (requiresPaymentRef && !paymentRef?.trim()) {
      setError('Payment reference is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      await onConfirm({ comment, paymentRef, paymentDate });
      setComment('');
      setPaymentRef('');
      setPaymentDate('');
      onClose();
    } catch (err) {
      setError(err?.message || 'Action failed');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full">
        <div className="p-5 border-b border-border">
          <h3 className="font-semibold text-foreground">{title}</h3>
          <p className="text-sm text-muted-foreground mt-1">{description}</p>
        </div>
        <div className="p-5 space-y-3">
          {requiresPaymentRef && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Payment Reference *</label>
              <input
                type="text"
                value={paymentRef}
                onChange={(e) => setPaymentRef(e?.target?.value)}
                placeholder="e.g., PAY-2026-001"
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}
          {requiresPaymentDate && (
            <div>
              <label className="text-sm font-medium text-foreground block mb-1">Payment Date</label>
              <input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e?.target?.value)}
                className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-foreground block mb-1">
              Comments {requiresComment ? '*' : '(Optional)'}
            </label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e?.target?.value)}
              placeholder="Add any comments..."
              rows={3}
              className="w-full px-3 py-2.5 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
            />
          </div>
          {error && <p className="text-xs text-error">{error}</p>}
        </div>
        <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant={variant} onClick={handleConfirm} disabled={loading}>
            {loading ? 'Processing...' : actionLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { isAdmin, isProjectManager, hasPermission, MODULE_KEYS, PERM_KEYS } = useRBAC();
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [actionModal, setActionModal] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [employees, setEmployees] = useState([]);
  const [selectedSalesEmployee, setSelectedSalesEmployee] = useState(null);
  const [savingSalesOwner, setSavingSalesOwner] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [syncChanges, setSyncChanges] = useState([]);
  const [applyingSync, setApplyingSync] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRollbackDialog, setShowRollbackDialog] = useState(false);
  const [rollingBack, setRollingBack] = useState(false);

  const loadTicket = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const data = await fetchIncentiveTicketById(id);
      setTicket(data);
      // Load sync changes alongside ticket
      const changes = await fetchSyncChanges(id);
      setSyncChanges(changes);
    } catch (err) {
      setError(err?.message || 'Failed to load ticket');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadEmployees = useCallback(async () => {
    try {
      const data = await fetchEmployees();
      setEmployees(data);
    } catch (err) {
      console.warn('[TicketDetails] Could not load employees:', err?.message);
    }
  }, []);

  useEffect(() => { loadTicket(); loadEmployees(); }, [loadTicket, loadEmployees]);

  // ── Realtime subscription: auto-reload ticket when the DB row is updated ──
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      ?.channel(`incentive_ticket_${id}`)
      ?.on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'incentive_tickets',
          filter: `id=eq.${id}`,
        },
        () => {
          // Silently reload the ticket data when the calculator syncs changes
          loadTicket();
        }
      )
      ?.subscribe();

    return () => {
      supabase?.removeChannel(channel);
    };
  }, [id, loadTicket]);
  // ─────────────────────────────────────────────────────────────────────────

  const handleSaveSalesOwner = async () => {
    if (!selectedSalesEmployee) return;
    setSavingSalesOwner(true);
    setError('');
    try {
      await setSalesOwner(id, {
        employeeId: selectedSalesEmployee?.id,
        employeeName: selectedSalesEmployee?.fullName,
        department: selectedSalesEmployee?.department || 'General',
        role: selectedSalesEmployee?.role || 'Employee',
      }, user?.id);
      setSuccessMsg(`Sales Deal Owner set to ${selectedSalesEmployee?.fullName}`);
      setSelectedSalesEmployee(null);
      await loadTicket();
    } catch (err) {
      setError(err?.message || 'Failed to set sales owner');
    } finally {
      setSavingSalesOwner(false);
    }
  };

  const handleValidateAndCalculate = async () => {
    setCalculating(true);
    setError('');
    try {
      await validateAndCalculate(id, user?.id);
      setSuccessMsg('Ticket validated and calculated successfully. Status updated to Calculated.');
      await loadTicket();
    } catch (err) {
      setError(err?.message || 'Validation failed');
    } finally {
      setCalculating(false);
    }
  };

  const handleAddMember = async (memberData) => {
    await addTicketMember(id, {
      ...memberData,
      department: memberData?.department || 'General',
      role: memberData?.role || 'Employee',
    }, user?.id);
    await loadTicket();
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm('Remove this team member? Incentives will be recalculated.')) return;
    try {
      await removeTicketMember(id, memberId, user?.id);
      await loadTicket();
    } catch (err) {
      setError(err?.message || 'Failed to remove member');
    }
  };

  const handleStatusAction = async ({ comment, paymentRef, paymentDate }) => {
    const { newStatus } = actionModal;
    const extraFields = {};
    if (paymentRef) extraFields.payment_reference = paymentRef;
    if (paymentDate) extraFields.payment_date = paymentDate;
    if (comment && newStatus === 'Rejected') extraFields.rejection_reason = comment;
    if (comment && newStatus === 'On Hold') extraFields.hold_reason = comment;
    if (comment && newStatus === 'Pending Team Assignment') extraFields.return_comment = comment;
    if (comment) extraFields.approval_comments = comment;
    // Pass user objects for global audit trail logging
    await updateTicketStatus(id, newStatus, user?.id, comment, extraFields, user, userProfile);
    setSuccessMsg(`Ticket status updated to "${newStatus}"`);
    await loadTicket();
  };

  const handleDeleteTicket = async () => {
    if (!isAdmin && !isProjectManager && !hasPermission(MODULE_KEYS?.INCENTIVE_TICKETS, PERM_KEYS?.DELETE)) {
      setError('You do not have permission to delete tickets.');
      setShowDeleteConfirm(false);
      return;
    }
    setDeleting(true);
    setError('');
    try {
      const result = await deleteIncentiveTicket(id, user?.id, user, userProfile);
      setSuccessMsg(
        result?.calculatorRolledBack
          ? `Ticket ${result?.ticketNumber} deleted. Calculator has been rolled back to Open state.`
          : `Ticket ${result?.ticketNumber} deleted successfully.`
      );
      setShowDeleteConfirm(false);
      // Navigate back to list after short delay so user sees the message
      setTimeout(() => navigate('/incentive-tickets'), 1500);
    } catch (err) {
      setError(err?.message || 'Failed to delete ticket');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleRollbackWorkflow = async (targetStage, reason) => {
    setRollingBack(true);
    setError('');
    try {
      await rollbackTicketWorkflow(id, targetStage, reason, user?.id, user, userProfile);
      setSuccessMsg(`Ticket rolled back to "${targetStage}" successfully. Responsible users have been notified.`);
      await loadTicket();
    } catch (err) {
      throw err; // Let the dialog handle the error display
    } finally {
      setRollingBack(false);
    }
  };

  const handleApplySyncUpdate = async () => {
    setApplyingSync(true);
    setError('');
    try {
      await applyPendingSyncUpdate(id, user?.id, user?.profile?.full_name || user?.email || '');
      setSuccessMsg('Calculator update applied. Ticket re-submitted for approval.');
      await loadTicket();
    } catch (err) {
      setError(err?.message || 'Failed to apply sync update');
    } finally {
      setApplyingSync(false);
    }
  };

  const openAction = (newStatus, title, description, actionLabel, variant = 'default', requiresComment = false, requiresPaymentRef = false, requiresPaymentDate = false) => {
    setActionModal({ newStatus, title, description, actionLabel, variant, requiresComment, requiresPaymentRef, requiresPaymentDate });
  };

  const projectMembers = ticket?.members?.filter(m => m?.memberType === 'PROJECT_TEAM') || [];
  const salesMembers = ticket?.members?.filter(m => m?.memberType === 'SALES') || [];
  const currentSalesOwner = salesMembers?.[0];

  const isLocked = ticket?.isLocked && user?.profile?.role !== 'admin';
  const canEdit = !isLocked && !['Paid', 'Rejected', 'Cancelled']?.includes(ticket?.status);
  const canValidate = canEdit && ['Pending Team Assignment', 'Calculated']?.includes(ticket?.status);
  const canSubmitForApproval = ticket?.status === 'Calculated';
  const canApprove = ticket?.status === 'Pending Approval';
  const canReject = ['Pending Approval', 'Approved']?.includes(ticket?.status);
  const canHold = ['Pending Approval', 'Approved']?.includes(ticket?.status);
  const canSendBack = ticket?.status === 'Pending Approval';
  const canMarkReadyForPayment = ticket?.status === 'Approved';
  const canMarkPaid = ticket?.status === 'Ready for Payment';
  const canApplySync = ticket?.sync_status === 'pending_update' && ['Pending Approval', 'Approved']?.includes(ticket?.status);

  // Role-based restrictions: delete and status change are Admin/Project Manager only
  const canDeleteTicket = (isAdmin || isProjectManager || hasPermission(MODULE_KEYS?.INCENTIVE_TICKETS, PERM_KEYS?.DELETE)) && !['Paid']?.includes(ticket?.status);
  const canChangeStatus = isAdmin || isProjectManager;

  // Rollback permission: Admin, Project Manager, or explicit workflow_rollback permission
  // Only available when ticket is at stage index >= 1 (i.e., has at least one previous stage)
  const WORKFLOW_STAGE_ORDER_LOCAL = ['Pending Team Assignment', 'Calculated', 'Pending Approval', 'Approved', 'Ready for Payment', 'Paid'];
  const canRollback = (isAdmin || hasPermission(MODULE_KEYS?.INCENTIVE_TICKETS, 'workflow_rollback')) &&
    WORKFLOW_STAGE_ORDER_LOCAL?.indexOf(ticket?.status) > 0;

  const currentStepIndex = STATUS_STEPS?.indexOf(ticket?.status);

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center py-20">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            <span className="text-sm text-muted-foreground">Loading ticket...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error && !ticket) {
    return (
      <MainLayout>
        <div className="flex flex-col items-center justify-center py-20 gap-4">
          <Icon name="AlertCircle" size={40} className="text-error" />
          <p className="text-foreground font-semibold">{error}</p>
          <Button variant="outline" onClick={() => navigate('/incentive-tickets')}>Back to Tickets</Button>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/incentive-tickets')} className="p-2 hover:bg-muted rounded-xl transition-colors">
              <Icon name="ArrowLeft" size={20} className="text-muted-foreground" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl md:text-2xl font-bold text-foreground">{ticket?.ticketNumber}</h1>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS?.[ticket?.status] || ''}`}>
                  {ticket?.status}
                </span>
                {ticket?.isLocked && <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs bg-muted text-muted-foreground"><Icon name="Lock" size={10} />Locked</span>}
              </div>
              <p className="text-sm text-muted-foreground mt-0.5">{ticket?.projectName}</p>
            </div>
          </div>
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" iconName="Printer" iconPosition="left" onClick={() => {
              // Open a dedicated print window to suppress browser header/footer (title, URL, date)
              const el = document.getElementById('ticket-print-area');
              if (!el) return;
              const printWindow = window.open('', '_blank', 'width=900,height=700');
              if (!printWindow) {
                // Fallback: show element and print in current window
                el.style.display = 'block';
                window.print();
                setTimeout(() => { el.style.display = 'none'; }, 1500);
                return;
              }
              printWindow?.document?.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${ticket?.ticketNumber || 'Project Incentive Ticket'}</title>
  <style>
    @page {
      size: A4 portrait;
      margin: 10mm 12mm 10mm 12mm;
    }
    html, body {
      margin: 0;
      padding: 0;
      font-family: Arial, sans-serif;
      color: #111;
      background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      height: 100%;
    }
    /* Suppress browser-generated header (about:blank / title) and footer (URL / page number) */
    @top-left { content: none !important; display: none !important; }
    @top-center { content: none !important; display: none !important; }
    @top-right { content: none !important; display: none !important; }
    @bottom-left { content: none !important; display: none !important; }
    @bottom-center { content: none !important; display: none !important; }
    @bottom-right { content: none !important; display: none !important; }
    /* Force the print area visible and flex so signatures push to bottom */
    #ticket-print-area {
      display: flex !important;
      flex-direction: column !important;
      min-height: 257mm !important;
    }
    /* Hide any URL/link text that browsers inject */
    a[href]::after { content: none !important; }
    abbr[title]::after { content: none !important; }
  </style>
</head>
<body>
${el?.innerHTML}
</body>
</html>`);
              printWindow.document.title = ticket?.ticketNumber || 'Project Incentive Ticket';
              printWindow?.document?.close();
              printWindow?.focus();
              setTimeout(() => {
                printWindow?.print();
                printWindow?.close();
              }, 400);
            }}>
              Print
            </Button>
            {canValidate && (
              <Button variant="default" iconName="Calculator" iconPosition="left" onClick={handleValidateAndCalculate} disabled={calculating}>
                {calculating ? 'Calculating...' : 'Validate & Calculate'}
              </Button>
            )}
            {/* Status change actions — restricted to Admin / Project Manager only */}
            {canChangeStatus && canSubmitForApproval && (
              <Button variant="default" iconName="Send" iconPosition="left"
                onClick={() => openAction('Pending Approval', 'Submit for Approval', 'Submit this ticket for management approval.', 'Submit for Approval')}>
                Submit for Approval
              </Button>
            )}
            {canChangeStatus && canApprove && (
              <Button variant="default" iconName="CheckCircle" iconPosition="left"
                onClick={() => openAction('Approved', 'Approve Ticket', 'Approve this incentive ticket.', 'Approve', 'default')}>
                Approve
              </Button>
            )}
            {canChangeStatus && canSendBack && (
              <Button variant="outline" iconName="RotateCcw" iconPosition="left"
                onClick={() => openAction('Pending Team Assignment', 'Send Back for Edit', 'Send this ticket back for editing.', 'Send Back', 'outline', true)}>
                Send Back
              </Button>
            )}
            {canChangeStatus && canMarkReadyForPayment && (
              <Button variant="default" iconName="CreditCard" iconPosition="left"
                onClick={() => openAction('Ready for Payment', 'Mark Ready for Payment', 'Mark this ticket as ready for payment processing.', 'Mark Ready', 'default')}>
                Ready for Payment
              </Button>
            )}
            {canChangeStatus && canMarkPaid && (
              <Button variant="default" iconName="Banknote" iconPosition="left"
                onClick={() => openAction('Paid', 'Mark as Paid', 'Confirm payment has been processed.', 'Mark as Paid', 'default', false, true, true)}>
                Mark as Paid
              </Button>
            )}
            {canChangeStatus && canHold && (
              <Button variant="outline" iconName="PauseCircle" iconPosition="left"
                onClick={() => openAction('On Hold', 'Put On Hold', 'Put this ticket on hold.', 'Put On Hold', 'outline', true)}>
                On Hold
              </Button>
            )}
            {canChangeStatus && canReject && (
              <Button variant="destructive" iconName="XCircle" iconPosition="left"
                onClick={() => openAction('Rejected', 'Reject Ticket', 'Reject this incentive ticket.', 'Reject', 'destructive', true)}>
                Reject
              </Button>
            )}
            {/* Delete ticket — Admin / Project Manager only, not allowed on Paid tickets */}
            {canDeleteTicket && (
              <Button
                variant="destructive"
                iconName="Trash2"
                iconPosition="left"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={deleting}
                title="Delete ticket and roll back calculator to Open state"
              >
                {deleting ? 'Deleting...' : 'Delete Ticket'}
              </Button>
            )}
            {/* Rollback Workflow — Admin or users with workflow_rollback permission */}
            {canRollback && (
              <Button
                variant="outline"
                iconName="RotateCcw"
                iconPosition="left"
                onClick={() => setShowRollbackDialog(true)}
                disabled={rollingBack}
                title="Roll back this ticket to a previous workflow stage"
                className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
              >
                {rollingBack ? 'Rolling Back...' : 'Rollback Workflow'}
              </Button>
            )}
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-card border border-border rounded-2xl shadow-2xl max-w-md w-full">
              <div className="p-5 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-error/15">
                    <Icon name="Trash2" size={18} className="text-error" />
                  </div>
                  <h3 className="font-semibold text-foreground">Delete Incentive Ticket</h3>
                </div>
              </div>
              <div className="p-5 space-y-3">
                <p className="text-sm text-foreground">
                  Are you sure you want to delete ticket <span className="font-mono font-semibold text-primary">{ticket?.ticketNumber}</span>?
                </p>
                <div className="p-3 bg-warning/10 border border-warning/30 rounded-xl text-warning text-sm flex items-start gap-2">
                  <Icon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                  <span>
                    This will permanently delete the ticket and <strong>roll back the linked calculator to Open state</strong>, allowing a new ticket to be created.
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">This action is logged in the audit trail and cannot be undone.</p>
              </div>
              <div className="flex items-center justify-end gap-3 p-5 border-t border-border">
                <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</Button>
                <Button variant="destructive" iconName="Trash2" iconPosition="left" onClick={handleDeleteTicket} disabled={deleting}>
                  {deleting ? 'Deleting...' : 'Delete & Rollback'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Rollback Workflow Dialog */}
        <RollbackWorkflowDialog
          isOpen={showRollbackDialog}
          onClose={() => setShowRollbackDialog(false)}
          onConfirm={handleRollbackWorkflow}
          currentStatus={ticket?.status || ''}
          ticketNumber={ticket?.ticketNumber || ''}
        />

        {/* Messages */}
        {successMsg && (
          <div className="flex items-start gap-3 p-4 bg-success/10 border border-success/30 rounded-xl text-success text-sm">
            <Icon name="CheckCircle" size={16} className="mt-0.5 shrink-0" />
            <span>{successMsg}</span>
            <button onClick={() => setSuccessMsg('')} className="ml-auto shrink-0 hover:opacity-70"><Icon name="X" size={14} /></button>
          </div>
        )}
        {error && (
          <div className="flex items-start gap-3 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-sm">
            <Icon name="AlertCircle" size={16} className="mt-0.5 shrink-0" />
            <span>{error}</span>
            <button onClick={() => setError('')} className="ml-auto shrink-0 hover:opacity-70"><Icon name="X" size={14} /></button>
          </div>
        )}

        {/* Status Timeline */}
        <div className="bg-card border border-border rounded-2xl p-4 shadow-sm">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            {STATUS_STEPS?.map((step, idx) => {
              const isCompleted = currentStepIndex > idx;
              const isCurrent = currentStepIndex === idx;
              const isSpecial = ['Rejected', 'On Hold', 'Cancelled']?.includes(ticket?.status);
              return (
                <React.Fragment key={step}>
                  <div className={`flex flex-col items-center gap-1 min-w-[90px] ${isSpecial && isCurrent ? 'opacity-50' : ''}`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all
                      ${isCompleted ? 'bg-success text-white' : isCurrent ? 'bg-primary text-white ring-2 ring-primary/30' : 'bg-muted text-muted-foreground'}`}>
                      {isCompleted ? <Icon name="Check" size={14} /> : idx + 1}
                    </div>
                    <span className={`text-xs text-center leading-tight ${isCurrent ? 'text-primary font-semibold' : isCompleted ? 'text-success' : 'text-muted-foreground'}`}>
                      {step?.replace(' Team', '')}
                    </span>
                  </div>
                  {idx < STATUS_STEPS?.length - 1 && (
                    <div className={`flex-1 h-0.5 min-w-[20px] ${isCompleted ? 'bg-success' : 'bg-border'}`} />
                  )}
                </React.Fragment>
              );
            })}
            {['Rejected', 'On Hold', 'Cancelled']?.includes(ticket?.status) && (
              <div className="ml-2 flex items-center gap-2">
                <div className="w-0.5 h-8 bg-border" />
                <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${STATUS_COLORS?.[ticket?.status] || ''}`}>
                  {ticket?.status}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-muted/30 p-1 rounded-xl border border-border w-fit">
          {['details', 'assignment', 'members', 'sync', 'audit']?.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all capitalize ${activeTab === tab ? 'bg-card shadow-sm text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab === 'audit' ? 'Audit Log' : tab === 'members' ? 'Team Members' : tab === 'assignment' ? 'Assignment' : tab === 'sync' ? (
                <span className="flex items-center gap-1.5">
                  Sync
                  {ticket?.sync_status === 'pending_update' && (
                    <span className="w-2 h-2 rounded-full bg-warning inline-block" />
                  )}
                </span>
              ) : 'Details'}
            </button>
          ))}
        </div>

        {/* DETAILS TAB */}
        {activeTab === 'details' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-primary to-accent">
                  <Icon name="Ticket" size={18} color="white" />
                </div>
                <h2 className="font-semibold text-foreground">Ticket Information</h2>
              </div>
              {/* Sync Status Badge */}
              {ticket?.calculationId && (
                <div className="mb-4 pb-4 border-b border-border/30">
                  <SyncStatusBadge
                    syncStatus={ticket?.sync_status || 'synced'}
                    lastSyncedAt={ticket?.last_synced_at || ticket?.updatedAt}
                    isAdjustment={ticket?.is_adjustment_ticket}
                    parentTicketNumber={ticket?.parent_ticket_number}
                    onApplyUpdate={handleApplySyncUpdate}
                    canApplyUpdate={canApplySync && !applyingSync}
                  />
                </div>
              )}
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Ticket ID', value: ticket?.ticketNumber, mono: true, color: 'text-primary' },
                  { label: 'Project Name', value: ticket?.projectName },
                  { label: 'Client Name', value: ticket?.clientName || '—' },
                  { label: 'Created By', value: ticket?.creator?.full_name || ticket?.creator?.email || '—' },
                  { label: 'Created Date', value: ticket?.createdAt ? new Date(ticket.createdAt)?.toLocaleDateString('en-GB') : '—' },
                  ticket?.calculatedAt && { label: 'Calculated At', value: new Date(ticket.calculatedAt)?.toLocaleString('en-GB') },
                  ticket?.submittedAt && { label: 'Submitted At', value: new Date(ticket.submittedAt)?.toLocaleString('en-GB') },
                  ticket?.approver && { label: 'Approved By', value: ticket?.approver?.full_name || ticket?.approver?.email },
                  ticket?.approvedAt && { label: 'Approved At', value: new Date(ticket.approvedAt)?.toLocaleString('en-GB') },
                  ticket?.paymentReference && { label: 'Payment Reference', value: ticket?.paymentReference, mono: true, color: 'text-success' },
                  ticket?.paymentDate && { label: 'Payment Date', value: ticket?.paymentDate },
                  ticket?.rejectionReason && { label: 'Rejection Reason', value: ticket?.rejectionReason, color: 'text-error' },
                  ticket?.holdReason && { label: 'Hold Reason', value: ticket?.holdReason, color: 'text-warning' },
                  ticket?.returnComment && { label: 'Return Comment', value: ticket?.returnComment },
                ]?.filter(Boolean)?.map(item => (
                  <div key={item?.label} className="flex justify-between items-center py-2 border-b border-border/30">
                    <span className="text-muted-foreground">{item?.label}</span>
                    <span className={`font-medium text-right max-w-[220px] ${item?.mono ? 'font-mono' : ''} ${item?.color || 'text-foreground'}`}>{item?.value}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-success to-emerald-600">
                  <Icon name="TrendingUp" size={18} color="white" />
                </div>
                <h2 className="font-semibold text-foreground">Financial Summary</h2>
              </div>
              <div className="space-y-3 text-sm">
                {[
                  { label: 'Project Value', value: formatOMR(ticket?.projectValue) },
                  { label: 'Total Cost', value: formatOMR(ticket?.totalCost) },
                  { label: 'Target Margin %', value: `${(ticket?.targetMarginPercentage || 0)?.toFixed(2)}%` },
                  { label: 'Target Margin Amount (Net Profit)', value: formatOMR(ticket?.targetMarginAmount), highlight: true, color: 'text-primary' },
                  { label: `Sales Incentive (${ticket?.salesPercentage}% of Net Profit)`, value: formatOMR(ticket?.salesIncentiveAmount) },
                  { label: `Project Team Incentive (${ticket?.projectTeamPercentage}% of Net Profit)`, value: formatOMR(ticket?.projectTeamIncentiveAmount) },
                ]?.map(item => (
                  <div key={item?.label} className="flex justify-between items-center py-2 border-b border-border/30">
                    <span className="text-muted-foreground text-xs">{item?.label}</span>
                    <span className={`font-medium ${item?.color || 'text-foreground'}`}>{item?.value}</span>
                  </div>
                ))}
                <div className="flex justify-between items-center py-2 border-b border-border/30 bg-error/5 rounded-lg px-2">
                  <span className="font-semibold text-foreground text-sm">Total Incentives</span>
                  <span className="font-bold text-error">{formatOMR(ticket?.totalIncentives)}</span>
                </div>
                <div className="flex justify-between items-center py-2 bg-success/5 rounded-lg px-2">
                  <span className="font-semibold text-foreground text-sm">Profit After Incentives</span>
                  <span className="font-bold text-success">{formatOMR(ticket?.profitAfterIncentives)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ASSIGNMENT TAB */}
        {activeTab === 'assignment' && (
          <div className="space-y-6">
            {/* Sales Deal Owner */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                  <Icon name="Briefcase" size={18} color="white" />
                </div>
                <div>
                  <h2 className="font-semibold text-foreground">Sales Deal Owner</h2>
                  <p className="text-xs text-muted-foreground">Person who closed the deal — receives {ticket?.salesPercentage}% of Net Profit = {formatOMR(ticket?.salesIncentiveAmount)}</p>
                </div>
              </div>

              {currentSalesOwner ? (
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
                        <Icon name="User" size={18} className="text-blue-400" />
                      </div>
                      <div>
                        <p className="font-semibold text-foreground">{currentSalesOwner?.employeeName}</p>
                        <p className="text-xs text-muted-foreground">{currentSalesOwner?.department || 'General'} · {currentSalesOwner?.role || 'Employee'}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-primary text-lg">{formatOMR(currentSalesOwner?.incentiveAmount)}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${currentSalesOwner?.paymentStatus === 'Paid' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {currentSalesOwner?.paymentStatus}
                      </span>
                    </div>
                  </div>
                  {canEdit && (
                    <div className="pt-2">
                      <p className="text-xs text-muted-foreground mb-2">Change Sales Deal Owner:</p>
                      <div className="flex gap-2">
                        <div className="flex-1">
                          <EmployeeSelect
                            employees={employees}
                            value={selectedSalesEmployee?.id}
                            onChange={setSelectedSalesEmployee}
                            placeholder="Select different employee..."
                          />
                        </div>
                        {selectedSalesEmployee && (
                          <Button variant="default" onClick={handleSaveSalesOwner} disabled={savingSalesOwner}>
                            {savingSalesOwner ? 'Saving...' : 'Update'}
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-muted-foreground">No Sales Deal Owner assigned yet. Select from Employee Pool:</p>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <EmployeeSelect
                        employees={employees}
                        value={selectedSalesEmployee?.id}
                        onChange={setSelectedSalesEmployee}
                        placeholder="Select Sales Deal Owner from Employee Pool..."
                      />
                    </div>
                    <Button variant="default" onClick={handleSaveSalesOwner} disabled={savingSalesOwner || !selectedSalesEmployee}>
                      {savingSalesOwner ? 'Saving...' : 'Assign'}
                    </Button>
                  </div>
                  {employees?.length === 0 && (
                    <p className="text-xs text-warning">No employees found in Employee Pool. Please add employees in Master Data first.</p>
                  )}
                </div>
              )}
            </div>

            {/* Project Team Members */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-border/50">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600">
                    <Icon name="Users" size={18} color="white" />
                  </div>
                  <div>
                    <h2 className="font-semibold text-foreground">Project Team Members</h2>
                    <p className="text-xs text-muted-foreground">
                      Total: {formatOMR(ticket?.projectTeamIncentiveAmount)} · {projectMembers?.length} member{projectMembers?.length !== 1 ? 's' : ''}
                      {projectMembers?.length > 0 && ` · ${formatOMR(ticket?.projectTeamIncentiveAmount / projectMembers?.length)} each`}
                    </p>
                  </div>
                </div>
                {canEdit && (
                  <Button variant="default" iconName="UserPlus" iconPosition="left" onClick={() => setShowAddMember(true)}>
                    Add Member
                  </Button>
                )}
              </div>

              {projectMembers?.length === 0 ? (
                <div className="text-center py-8">
                  <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No project team members added yet</p>
                  {canEdit && (
                    <Button variant="default" iconName="UserPlus" iconPosition="left" className="mt-3" onClick={() => setShowAddMember(true)}>
                      Add First Member
                    </Button>
                  )}
                </div>
              ) : (
                <div className="space-y-2">
                  {projectMembers?.map((member, idx) => (
                    <div key={member?.id} className="flex items-center justify-between p-3 bg-muted/20 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center text-xs font-bold text-purple-400">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-medium text-foreground text-sm">{member?.employeeName}</p>
                          <p className="text-xs text-muted-foreground">{member?.department || 'General'} · {member?.role || 'Employee'}</p>
                          {member?.workDescription && <p className="text-xs text-muted-foreground italic mt-0.5">{member?.workDescription}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="font-bold text-primary text-sm">{formatOMR(member?.incentiveAmount)}</p>
                          <p className="text-xs text-muted-foreground">{member?.sharePercentage?.toFixed(1)}% share</p>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${member?.paymentStatus === 'Paid' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                          {member?.paymentStatus}
                        </span>
                        {canEdit && (
                          <button onClick={() => handleRemoveMember(member?.id)} className="p-1.5 hover:bg-error/10 rounded-lg transition-colors" title="Remove">
                            <Icon name="Trash2" size={14} className="text-error" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Validate & Calculate CTA */}
            {canValidate && (
              <div className="bg-gradient-to-r from-primary/10 to-accent/10 border border-primary/20 rounded-2xl p-5 flex items-center justify-between gap-4">
                <div>
                  <p className="font-semibold text-foreground">Ready to Calculate?</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    Ensure Sales Deal Owner and at least one Project Team Member are assigned, then click Validate & Calculate.
                  </p>
                </div>
                <Button variant="default" iconName="Calculator" iconPosition="left" onClick={handleValidateAndCalculate} disabled={calculating}>
                  {calculating ? 'Calculating...' : 'Validate & Calculate'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* TEAM MEMBERS TAB */}
        {activeTab === 'members' && (
          <div className="space-y-4">
            <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-foreground">All Incentive Members</h2>
                  <p className="text-xs text-muted-foreground mt-0.5">Sales + Project Team breakdown</p>
                </div>
              </div>
              {ticket?.members?.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground text-sm">No members assigned yet. Go to the Assignment tab to add members.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Employee</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Type</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Department</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Role</th>
                        <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Share %</th>
                        <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Incentive</th>
                        <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Payment</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {ticket?.members?.map(member => (
                        <tr key={member?.id} className="hover:bg-muted/10 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground">{member?.employeeName}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${member?.memberType === 'SALES' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'}`}>
                              {member?.memberType === 'SALES' ? 'Sales' : 'Project Team'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{member?.department || 'General'}</td>
                          <td className="px-4 py-3 text-muted-foreground">{member?.role || 'Employee'}</td>
                          <td className="px-4 py-3 text-right font-medium text-foreground">{member?.sharePercentage?.toFixed(2)}%</td>
                          <td className="px-4 py-3 text-right font-bold text-primary">{formatOMR(member?.incentiveAmount)}</td>
                          <td className="px-4 py-3 text-center">
                            <span className={`text-xs px-2 py-0.5 rounded-full ${member?.paymentStatus === 'Paid' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                              {member?.paymentStatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="bg-muted/20 border-t border-border">
                        <td colSpan={5} className="px-4 py-3 font-semibold text-foreground">Total ({ticket?.members?.length} members)</td>
                        <td className="px-4 py-3 text-right font-bold text-primary">{formatOMR(ticket?.totalIncentives)}</td>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SYNC TAB */}
        {activeTab === 'sync' && (
          <div className="space-y-6">
            {/* Sync Status Card */}
            <div className="bg-card border border-border rounded-2xl p-6 shadow-sm">
              <div className="flex items-center gap-3 mb-5 pb-4 border-b border-border/50">
                <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500">
                  <Icon name="RefreshCw" size={18} color="white" />
                </div>
                <div>
                  <h2 className="font-semibold text-foreground">Calculator Sync Status</h2>
                  <p className="text-xs text-muted-foreground">Live relationship with Project Calculator</p>
                </div>
              </div>

              <div className="space-y-4">
                <SyncStatusBadge
                  syncStatus={ticket?.sync_status || 'synced'}
                  lastSyncedAt={ticket?.last_synced_at || ticket?.updatedAt}
                  isAdjustment={ticket?.is_adjustment_ticket}
                  parentTicketNumber={ticket?.parent_ticket_number}
                  onApplyUpdate={handleApplySyncUpdate}
                  canApplyUpdate={canApplySync && !applyingSync}
                />

                {/* Status Logic Explanation */}
                <div className="mt-4 p-4 bg-muted/20 rounded-xl border border-border/50 text-sm space-y-2">
                  <p className="font-semibold text-foreground text-xs uppercase tracking-wide mb-2">Sync Rules</p>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-success mt-1 flex-shrink-0" />
                    <span><strong className="text-foreground">Draft / Pending / Calculated</strong> — Auto-updates when calculator changes</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-warning mt-1 flex-shrink-0" />
                    <span><strong className="text-foreground">Pending Approval / Approved</strong> — Flags as Pending Update, requires re-approval</span>
                  </div>
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <span className="w-2 h-2 rounded-full bg-error mt-1 flex-shrink-0" />
                    <span><strong className="text-foreground">Paid</strong> — Ticket is locked; an Adjustment Ticket is created for any changes</span>
                  </div>
                </div>

                {/* Calculator snapshot values */}
                {(ticket?.calculator_project_value > 0 || ticket?.calculator_gross_profit > 0) && (
                  <div className="mt-4">
                    <p className="text-xs font-semibold text-foreground uppercase tracking-wide mb-2">Last Calculator Snapshot</p>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: 'Project Value', value: formatOMR(ticket?.calculator_project_value) },
                        { label: 'Contract Value', value: formatOMR(ticket?.calculator_contract_value || ticket?.calculator_project_value) },
                        { label: 'Gross Profit', value: formatOMR(ticket?.calculator_gross_profit) },
                        { label: 'Incentive %', value: `${ticket?.calculator_incentive_percentage || 2}%` },
                      ]?.map(item => (
                        <div key={item?.label} className="p-3 bg-muted/20 rounded-xl border border-border/50">
                          <p className="text-xs text-muted-foreground">{item?.label}</p>
                          <p className="font-semibold text-foreground text-sm mt-0.5">{item?.value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Change History */}
            <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold text-foreground">Change History</h2>
                <p className="text-xs text-muted-foreground mt-0.5">All calculator-driven changes with old/new values</p>
              </div>
              {syncChanges?.length === 0 ? (
                <div className="p-10 text-center text-muted-foreground text-sm">
                  <Icon name="RefreshCw" size={28} className="mx-auto mb-2 opacity-30" />
                  No sync changes recorded yet
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border bg-muted/30">
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Field</th>
                        <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Previous Value</th>
                        <th className="text-right px-4 py-3 font-semibold text-muted-foreground">New Value</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Changed By</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Date</th>
                        <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {syncChanges?.map(change => (
                        <tr key={change?.id} className="hover:bg-muted/10 transition-colors">
                          <td className="px-4 py-3 font-medium text-foreground">{change?.field_name}</td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-error bg-error/10 px-2 py-0.5 rounded font-mono text-xs">
                              {change?.old_value ? `OMR ${parseFloat(change?.old_value)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}` : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-success bg-success/10 px-2 py-0.5 rounded font-mono text-xs">
                              {change?.new_value ? `OMR ${parseFloat(change?.new_value)?.toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}` : '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">{change?.changed_by_name || 'System'}</td>
                          <td className="px-4 py-3 text-muted-foreground text-xs">
                            {change?.changed_at ? new Date(change?.changed_at)?.toLocaleString('en-GB') : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                              change?.sync_action === 'auto_update' ? 'bg-success/10 text-success' :
                              change?.sync_action === 'pending_approval' ? 'bg-warning/10 text-warning' :
                              change?.sync_action === 'adjustment_ticket'? 'bg-blue-500/10 text-blue-400' : 'bg-muted text-muted-foreground'
                            }`}>
                              {change?.sync_action === 'auto_update' ? 'Auto Updated' :
                               change?.sync_action === 'pending_approval' ? 'Pending Approval' :
                               change?.sync_action === 'adjustment_ticket'? 'Adjustment Ticket' : change?.sync_action ||'—'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* AUDIT TAB */}
        {activeTab === 'audit' && (
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-border">
              <h2 className="font-semibold text-foreground">Audit Log</h2>
              <p className="text-xs text-muted-foreground mt-0.5">All changes and actions recorded</p>
            </div>
            {ticket?.auditLogs?.length === 0 ? (
              <div className="p-10 text-center text-muted-foreground text-sm">No audit records found</div>
            ) : (
              <div className="divide-y divide-border">
                {ticket?.auditLogs?.map(log => (
                  <div key={log?.id} className="flex items-start gap-4 p-4 hover:bg-muted/10 transition-colors">
                    <div className="p-2 rounded-lg bg-muted/30 flex-shrink-0">
                      <Icon name="Activity" size={14} className="text-muted-foreground" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground text-sm">{log?.action?.replace(/_/g, ' ')}</span>
                        {log?.field_changed && <span className="text-xs text-muted-foreground">· {log?.field_changed}</span>}
                      </div>
                      {(log?.old_value || log?.new_value) && (
                        <div className="flex items-center gap-2 mt-1 text-xs">
                          {log?.old_value && <span className="text-error bg-error/10 px-2 py-0.5 rounded">{log?.old_value}</span>}
                          {log?.old_value && log?.new_value && <Icon name="ArrowRight" size={10} className="text-muted-foreground" />}
                          {log?.new_value && <span className="text-success bg-success/10 px-2 py-0.5 rounded">{log?.new_value}</span>}
                        </div>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {log?.performer?.full_name || log?.performer?.email || 'System'} · {log?.created_at ? new Date(log.created_at)?.toLocaleString('en-GB') : ''}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <AddMemberModal
        isOpen={showAddMember}
        onClose={() => setShowAddMember(false)}
        onAdd={handleAddMember}
        employees={employees}
      />
      {actionModal && (
        <ActionModal
          isOpen={true}
          onClose={() => setActionModal(null)}
          onConfirm={handleStatusAction}
          title={actionModal?.title}
          description={actionModal?.description}
          actionLabel={actionModal?.actionLabel}
          variant={actionModal?.variant}
          requiresComment={actionModal?.requiresComment}
          requiresPaymentRef={actionModal?.requiresPaymentRef}
          requiresPaymentDate={actionModal?.requiresPaymentDate}
        />
      )}
      {/* Print Template */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm;
          }
          body * { visibility: hidden !important; }
          #ticket-print-area, #ticket-print-area * { visibility: visible !important; }
          #ticket-print-area { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; background: white !important; color: black !important; }
          .no-print { display: none !important; }
          /* Suppress browser-generated header/footer (URL, date, page number, title) */
          html { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
        }
      `}</style>
      <TicketPrintTemplate
        ticket={ticket}
        members={ticket?.members || []}
        generatedBy={user?.profile?.full_name || user?.email || 'System'}
      />
    </MainLayout>
  );
};

export default TicketDetails;
